// Consent Page - Stage 04 - VISUALLY UPDATED with InsureVision Colors
// Collects signer details and initiates DocuSign envelope
// UPDATED: Added demo mode support
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useOnboarding } from '../../context/OnboardingContext'
import { useAuth } from '../../context/AuthContext'
import { useDemoMode } from '../../hooks/useDemoMode'
import { n8nApi } from '../../services/n8nApi'

const ConsentPage = () => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { prospectData, transitionToStage, STAGES, effectiveUid, actingUid, isImpersonating } = useOnboarding()
  const { isDemoMode, simulateSuccess } = useDemoMode()
  const [showDetails, setShowDetails] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errors, setErrors] = useState({})

  const [formData, setFormData] = useState({
    signer_name: prospectData.fullName || '',
    signer_email: prospectData.email || user?.email || '',
    signer_title: '',
    legal_entity_name: prospectData.companyName || '',
    legal_entity_address: ''
  })

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }))
    }
  }

  const validate = () => {
    const newErrors = {}
    
    if (!formData.signer_name.trim()) {
      newErrors.signer_name = 'Signer name is required'
    }
    if (!formData.signer_email.trim()) {
      newErrors.signer_email = 'Signer email is required'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.signer_email)) {
      newErrors.signer_email = 'Please enter a valid email'
    }
    if (!formData.signer_title.trim()) {
      newErrors.signer_title = 'Job title is required'
    }
    if (!formData.legal_entity_name.trim()) {
      newErrors.legal_entity_name = 'Legal entity name is required'
    }
    if (!formData.legal_entity_address.trim()) {
      newErrors.legal_entity_address = 'Registered address is required'
    }
    
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validate()) return

    setIsSubmitting(true)
    setErrors({})

    // DEMO MODE: Simulate successful submission
    if (isDemoMode) {
      simulateSuccess(() => {
        setIsSubmitting(false)
        transitionToStage(STAGES.CONSENT_PENDING)
        navigate('/onboarding/consent-pending')
      }, 1500)
      return
    }

    try {
      // Use effectiveUid for the prospect being assisted
      // Pass actingUid for audit trail when impersonating
      const response = await n8nApi.initiateConsent(effectiveUid, formData, {
        acting: actingUid
      })

      if (response.success) {
        await transitionToStage(STAGES.CONSENT_PENDING)
        navigate('/onboarding/consent-pending')
      } else {
        setErrors({ submit: response.error || 'Failed to send agreement. Please try again.' })
      }
    } catch (error) {
      console.error('Error initiating consent:', error)
      setErrors({ submit: 'An error occurred. Please try again.' })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="max-w-2xl mx-auto animate-fade-in">
      <div className="text-center mb-8">
        <h1 className="text-2xl sm:text-3xl font-display font-bold text-brand-950">
          Data Access Agreement
        </h1>
        <p className="mt-3 text-slate-600">
          To analyze your dashcam footage and generate your personalized demo, we need your
          consent via DocuSign to access a selection of your dashcam video clips.
        </p>
      </div>

      {/* What we'll access - with brand colors */}
      <div className="card mb-6">
        <h2 className="text-lg font-semibold text-brand-950 mb-4">What we'll access</h2>
        <ul className="space-y-3">
          <li className="flex items-start gap-3">
            <svg className="w-5 h-5 text-accent-600 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span className="text-slate-700">A sample of recent dashcam video clips for risk pattern analysis. We will only access forward looking clips (cabin / rear video clips not needed)</span>
          </li>
          <li className="flex items-start gap-3">
            <svg className="w-5 h-5 text-accent-600 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span className="text-slate-700">Associated safety event data and contextual information</span>
          </li>
          <li className="flex items-start gap-3">
            <svg className="w-5 h-5 text-accent-600 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span className="text-slate-700">Basic fleet and driver information for context</span>
          </li>
        </ul>

        <button
          onClick={() => setShowDetails(!showDetails)}
          className="mt-4 text-accent-600 hover:text-accent-700 font-medium text-sm flex items-center gap-1 transition-colors"
        >
          {showDetails ? 'Hide' : 'Show'} agreement text
          <svg 
            className={`w-4 h-4 transition-transform ${showDetails ? 'rotate-180' : ''}`} 
            fill="none" viewBox="0 0 24 24" stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {showDetails && (
          <div className="mt-4 p-4 bg-slate-50 rounded-lg text-sm text-slate-700 space-y-4 animate-fade-in">
            <p><strong>Purpose:</strong> You are providing InsureVision with an API access token so InsureVision can access your data solely to prepare and deliver a product demonstration using your data.</p>
            
            <p><strong>Your Consent:</strong> By signing the agreement, you confirm that you have the right to share the API access token with InsureVision and consent to giving InsureVision API-based access to a selection of your data for the purpose described above.</p>
            
            <p><strong>Data Protection:</strong> InsureVision agrees to access your data only for the demonstration, protect your data with reasonable security measures, not share your data with third parties without written permission, and delete or return your data upon request or within 30 days after the demonstration.</p>
            
            <p><strong>Data Ownership:</strong> You retain full ownership of your data. This Agreement does not transfer any data rights to InsureVision.</p>
          </div>
        )}
      </div>

      {/* Signer Details Form */}
      <div className="card">
        <h2 className="text-lg font-semibold text-brand-950 mb-4">Authorized Signatory Details</h2>
        <p className="text-sm text-slate-600 mb-6">
          The agreement will be sent to this person for electronic signature via DocuSign.
        </p>

        {errors.submit && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200">
            <p className="text-sm text-red-700">{errors.submit}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Signer Name */}
          <div>
            <label htmlFor="signer_name" className="label">
              Full Name <span className="text-accent-600">*</span>
            </label>
            <input
              id="signer_name"
              name="signer_name"
              type="text"
              value={formData.signer_name}
              onChange={handleChange}
              className={`input-field ${errors.signer_name ? 'border-red-300' : ''}`}
              placeholder="Jane Smith"
            />
            {errors.signer_name && (
              <p className="mt-1 text-sm text-red-600">{errors.signer_name}</p>
            )}
          </div>

          {/* Signer Email */}
          <div>
            <label htmlFor="signer_email" className="label">
              Email Address <span className="text-accent-600">*</span>
            </label>
            <input
              id="signer_email"
              name="signer_email"
              type="email"
              value={formData.signer_email}
              onChange={handleChange}
              className={`input-field ${errors.signer_email ? 'border-red-300' : ''}`}
              placeholder="jane.smith@company.com"
            />
            {errors.signer_email && (
              <p className="mt-1 text-sm text-red-600">{errors.signer_email}</p>
            )}
            <p className="mt-1 text-xs text-slate-500">
              The DocuSign agreement will be sent to this email address
            </p>
          </div>

          {/* Signer Title */}
          <div>
            <label htmlFor="signer_title" className="label">
              Job Title <span className="text-accent-600">*</span>
            </label>
            <input
              id="signer_title"
              name="signer_title"
              type="text"
              value={formData.signer_title}
              onChange={handleChange}
              className={`input-field ${errors.signer_title ? 'border-red-300' : ''}`}
              placeholder="Fleet Manager"
            />
            {errors.signer_title && (
              <p className="mt-1 text-sm text-red-600">{errors.signer_title}</p>
            )}
          </div>

          {/* Legal Entity Name */}
          <div>
            <label htmlFor="legal_entity_name" className="label">
              Legal Entity Name <span className="text-accent-600">*</span>
            </label>
            <input
              id="legal_entity_name"
              name="legal_entity_name"
              type="text"
              value={formData.legal_entity_name}
              onChange={handleChange}
              className={`input-field ${errors.legal_entity_name ? 'border-red-300' : ''}`}
              placeholder="Acme Transport Ltd"
            />
            {errors.legal_entity_name && (
              <p className="mt-1 text-sm text-red-600">{errors.legal_entity_name}</p>
            )}
            <p className="mt-1 text-xs text-slate-500">
              The registered company name as it should appear on the agreement
            </p>
          </div>

          {/* Legal Entity Address */}
          <div>
            <label htmlFor="legal_entity_address" className="label">
              Registered Address <span className="text-accent-600">*</span>
            </label>
            <textarea
              id="legal_entity_address"
              name="legal_entity_address"
              rows={3}
              value={formData.legal_entity_address}
              onChange={handleChange}
              className={`input-field ${errors.legal_entity_address ? 'border-red-300' : ''}`}
              placeholder={"123 Business Park\nLondon\nSW1A 1AA"}
            />
            {errors.legal_entity_address && (
              <p className="mt-1 text-sm text-red-600">{errors.legal_entity_address}</p>
            )}
          </div>

          {/* Submit - Magenta accent button */}
          <div className="pt-4">
            <button
              type="submit"
              disabled={isSubmitting}
              className="btn-accent w-full text-lg py-4"
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Sending Agreement...
                </span>
              ) : (
                'Send Agreement for Signature'
              )}
            </button>
          </div>
        </form>

        <p className="mt-4 text-center text-sm text-slate-500">
          By submitting, a DocuSign agreement will be sent to the signatory's email
        </p>
      </div>
    </div>
  )
}

export default ConsentPage
